import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, ChevronDown, LogOut, Activity, Ticket, UtensilsCrossed, Sparkles, Car, Users, ShieldAlert } from 'lucide-react';
import { LOGO_URL } from '../../mock/mock';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { SosTriggerModal } from '../../features/sos';

const Navbar = () => {
  const { user, logout } = useAuth();
  const settings = useSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [sosOpen, setSosOpen] = useState(false);
  const logoSrc = settings?.general?.logo_url || LOGO_URL;
  const siteName = settings?.general?.site_name || 'TrainPanda';
  const mods = settings?.modules || {};

  const allLinks = [
    { to: '/live-train-status', label: 'Live Status', icon: Activity, key: 'live_status' },
    { to: '/pnr-status', label: 'PNR Status', icon: Ticket, key: 'pnr_status' },
    { to: '/food-on-train', label: 'Food', icon: UtensilsCrossed, key: 'food_on_train' },
    { to: '/cabs', label: 'Cabs', icon: Car, key: 'cab_booking' },
    { to: '/stories', label: 'Stories', icon: Sparkles, key: 'stories' },
    { to: '/family', label: 'Family & SOS', icon: Users, key: 'family' },
  ];
  // Default to true if flag missing (back-compat for older settings docs)
  const links = allLinks.filter(l => mods[l.key] !== false);

  const isActive = (to) => loc.pathname === to || (to !== '/' && loc.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 tp-glass border-b border-tp-violet/10">
      <div className="max-w-7xl mx-auto px-3 md:px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <img src={logoSrc} alt={siteName} className="h-10 md:h-11 w-auto group-hover:scale-105 transition-transform" />
        </Link>

        <nav className="hidden md:flex items-center gap-1 bg-white/60 rounded-full p-1 border border-tp-violet/10">
          {links.map(l => {
            const active = isActive(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace('/', '')}`}
                className={`px-3.5 py-1.5 text-sm font-bold flex items-center gap-1.5 rounded-full transition-all ${
                  active
                    ? 'bg-gradient-violet text-white shadow-tp-violet'
                    : 'text-tp-ink hover:bg-tp-violet-soft'
                }`}
              >
                <l.icon size={14} /> {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {/* Prominent Emergency SOS button in Navbar (matches floating widget style) */}
          <button
            onClick={() => setSosOpen(true)}
            className="group relative flex items-center gap-1.5 px-3 md:px-3.5 py-1.5 rounded-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-black shadow-lg shadow-red-600/40 hover:shadow-xl hover:scale-105 transition-all duration-200 border border-white/50"
            title="Trigger Emergency SOS Alert"
            aria-label="Emergency SOS Alert"
          >
            {/* Pulsing ring animation */}
            <span className="absolute -inset-0.5 rounded-full bg-red-500 opacity-40 animate-ping pointer-events-none"></span>

            <div className="relative flex items-center justify-center">
              <ShieldAlert className="w-4 h-4 text-white animate-pulse" />
            </div>

            <div className="flex flex-col text-left leading-none">
              <span className="text-[8px] uppercase font-black tracking-wider text-yellow-300">
                Emergency
              </span>
              <span className="text-xs font-extrabold tracking-tight">
                SOS Alert
              </span>
            </div>
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="hidden md:flex items-center gap-1.5 bg-tp-violet-soft hover:bg-tp-violet/20 px-3 py-1.5 rounded-full text-sm font-bold text-tp-violet-deep">
                <User size={14} /> {user.name || user.mobile} <ChevronDown size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => nav('/family')}>
                  <Users size={14} className="mr-2 text-orange-600" /> Family Contacts ({user.mobile ? 'SSO' : ''})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav('/my-orders')}>My Orders</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600"><LogOut size={14} className="mr-2" /> Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="hidden md:inline-flex">
              <Button className="btn-violet rounded-full px-5 h-9 text-sm">Login</Button>
            </Link>
          )}

          {/* Mobile: compact account icon (module tabs + bottom nav handle the rest) */}
          {user ? (
            <Link to="/my-orders" aria-label="My account" className="md:hidden p-2 rounded-full bg-tp-violet-soft text-tp-violet-deep">
              <User size={20} />
            </Link>
          ) : (
            <Link to="/login" aria-label="Login" className="md:hidden">
              <Button className="btn-violet rounded-full px-4 h-9 text-sm">Login</Button>
            </Link>
          )}
        </div>
      </div>
      <SosTriggerModal isOpen={sosOpen} onClose={() => setSosOpen(false)} />
    </header>
  );
};

export default Navbar;
